import type { JSX, KeyboardEvent } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Section } from '../VueDAigle/composants.js';
import commun from '../VueDAigle/commun.module.css';
import { useFormats } from '../VueDAigle/formats.js';
import { MA_PART, RESTE, styleRang } from './donnees.js';
import type { LienFlux, NoeudPartie, SchemaFlux } from './donnees.js';
import styles from './Comptabilite.module.css';
import { useNomType } from './nomsTypes.js';
import { useLargeur } from './useLargeur.js';

/** Écarte des repères qui se chevauchent, sans les sortir de la hauteur disponible. */
export function etaler(centres: readonly number[], ecartMin: number, hauteur: number): number[] {
  const resultat = [...centres];
  for (let i = 1; i < resultat.length; i += 1) {
    const minimum = (resultat[i - 1] ?? 0) + ecartMin;
    if ((resultat[i] ?? 0) < minimum) resultat[i] = minimum;
  }
  const depasse = (resultat[resultat.length - 1] ?? 0) - (hauteur - ecartMin / 2);
  if (depasse > 0) {
    for (let i = 0; i < resultat.length; i += 1) resultat[i] = (resultat[i] ?? 0) - depasse;
    for (let i = resultat.length - 2; i >= 0; i -= 1) {
      const maximum = (resultat[i + 1] ?? 0) - ecartMin;
      if ((resultat[i] ?? 0) > maximum) resultat[i] = maximum;
    }
  }
  return resultat;
}

function tronquer(texte: string, max: number): string {
  return texte.length > max ? `${texte.slice(0, max - 1).trimEnd()}…` : texte;
}

interface Noeud {
  id: string;
  y: number;
  h: number;
  valeur: number;
}

interface Trace {
  cle: string;
  lien: LienFlux;
  d: string;
  epaisseur: number;
  rang: number | null;
}

interface Disposition {
  W: number;
  H: number;
  etroit: boolean;
  nw: number;
  xG: number;
  xD: number;
  gauche: Noeud[];
  droite: Noeud[];
  traces: Trace[];
  reperesG: number[];
  reperesD: number[];
}

const cleLien = (lien: Pick<LienFlux, 'service' | 'partie'>): string =>
  `${lien.service}|${lien.partie}`;

/**
 * Dessine le diagramme À L'ÉCHELLE : la hauteur d'un nœud et l'épaisseur d'un
 * flux sont proportionnelles au montant HT (un minimum de quelques pixels
 * garde visible ce qui est minuscule). Un flux part du service, arrive à la
 * partie, et les flux s'empilent dans l'ordre des nœuds.
 */
export function disposer(schema: SchemaFlux, largeur: number): Disposition {
  const etroit = largeur < 520;
  const W = Math.max(largeur, 280);
  const padG = etroit ? 88 : 160;
  const padD = etroit ? 104 : 176;
  const nw = 12;
  const H = etroit ? 290 : 340;
  const ecart = 10;
  const maxN = Math.max(schema.services.length, schema.parties.length);
  const echelle = (H - ecart * (maxN - 1)) / schema.totalCentimes;

  const empiler = (valeurs: number[]): Array<{ y: number; h: number }> => {
    const hauteurs = valeurs.map((v) => Math.max(v * echelle, 3));
    const somme = hauteurs.reduce((total, h) => total + h, 0) + ecart * (valeurs.length - 1);
    let y = (H - somme) / 2;
    return hauteurs.map((h) => {
      const noeud = { y, h };
      y += h + ecart;
      return noeud;
    });
  };

  const gauche: Noeud[] = empiler(schema.services.map((s) => s.htCentimes)).map((p, i) => ({
    id: schema.services[i]!.service,
    valeur: schema.services[i]!.htCentimes,
    ...p,
  }));
  const droite: Noeud[] = empiler(schema.parties.map((p) => p.montantCentimes)).map((p, i) => ({
    id: schema.parties[i]!.id,
    valeur: schema.parties[i]!.montantCentimes,
    ...p,
  }));

  const xG = padG;
  const xD = W - padD - nw;
  const decalG = new Map<string, number>();
  const decalD = new Map<string, number>();
  const traces: Trace[] = [];
  const liens = new Map(schema.liens.map((lien) => [cleLien(lien), lien]));
  for (const partie of schema.parties) {
    const noeudD = droite.find((n) => n.id === partie.id)!;
    for (const service of schema.services) {
      const lien = liens.get(cleLien({ service: service.service, partie: partie.id }));
      if (!lien) continue;
      const noeudG = gauche.find((n) => n.id === service.service)!;
      const h = lien.montantCentimes * echelle;
      const y0 = noeudG.y + (decalG.get(noeudG.id) ?? 0) + h / 2;
      const y1 = noeudD.y + (decalD.get(noeudD.id) ?? 0) + h / 2;
      decalG.set(noeudG.id, (decalG.get(noeudG.id) ?? 0) + h);
      decalD.set(noeudD.id, (decalD.get(noeudD.id) ?? 0) + h);
      const x0 = xG + nw;
      const milieu = (x0 + xD) / 2;
      traces.push({
        cle: cleLien(lien),
        lien,
        d: `M${x0.toFixed(1)},${y0.toFixed(1)} C${milieu.toFixed(1)},${y0.toFixed(1)} ${milieu.toFixed(1)},${y1.toFixed(1)} ${xD.toFixed(1)},${y1.toFixed(1)}`,
        epaisseur: Math.max(h, 1),
        rang: partie.rang,
      });
    }
  }

  return {
    W,
    H,
    etroit,
    nw,
    xG,
    xD,
    gauche,
    droite,
    traces,
    reperesG: etaler(
      gauche.map((n) => n.y + n.h / 2),
      28,
      H,
    ),
    reperesD: etaler(
      droite.map((n) => n.y + n.h / 2),
      28,
      H,
    ),
  };
}

/**
 * Le flux du HT : de chaque service vers chaque partie, plus ce qui n'est pas
 * réparti. Un flux se lit au survol, au focus (clavier) et au clic ; le même
 * contenu est en tableau dans le `<details>`, pour qui n'a pas la souris ou
 * veut le centime.
 */
export function Flux({
  schema,
  mois,
  associe,
  numero,
}: {
  schema: SchemaFlux;
  mois: string;
  associe: boolean;
  numero: string;
}): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const [hote, largeur] = useLargeur<HTMLDivElement>();
  const [survol, setSurvol] = useState<string | null>(null);
  const [epingle, setEpingle] = useState<string | null>(null);

  const nomType = useNomType();
  const nomService = (service: string, court: boolean): string => nomType(service, court);
  const nomPartie = (partie: NoeudPartie): string => {
    if (partie.id === MA_PART) return t('comptabilite.flux.maPart');
    if (partie.id === RESTE) {
      return associe ? t('comptabilite.flux.resteHt') : t('comptabilite.flux.nonReparti');
    }
    return partie.libelle ?? '';
  };

  const parties = useMemo(() => new Map(schema.parties.map((p) => [p.id, p])), [schema.parties]);
  const dispo = useMemo(
    () => (schema.totalCentimes > 0 ? disposer(schema, largeur) : null),
    [schema, largeur],
  );

  const phrase = (lien: LienFlux): string =>
    t('comptabilite.flux.lecture', {
      service: nomService(lien.service, false),
      partie: nomPartie(parties.get(lien.partie)!),
      montant: f.euros(lien.montantCentimes),
      part: f.pourcent(lien.partDuService),
    });

  const actif = survol ?? epingle;
  const lienActif =
    actif === null ? null : (schema.liens.find((l) => cleLien(l) === actif) ?? null);

  const surClavier = (evenement: KeyboardEvent, cle: string): void => {
    if (evenement.key !== 'Enter' && evenement.key !== ' ') return;
    evenement.preventDefault();
    setEpingle((courant) => (courant === cle ? null : cle));
  };

  return (
    <Section
      id="flux"
      numero={numero}
      titre={t('comptabilite.flux.titre')}
      note={associe ? t('comptabilite.flux.noteAssocie') : t('comptabilite.flux.noteDirection')}
    >
      <div ref={hote} className={styles.fluxHote}>
        {dispo === null ? (
          <p className={commun.note}>{t('comptabilite.flux.vide')}</p>
        ) : (
          <svg
            className={[styles.fluxSvg, actif !== null ? styles.fluxSur : ''].join(' ')}
            viewBox={`0 0 ${String(dispo.W)} ${String(dispo.H)}`}
            width={dispo.W}
            height={dispo.H}
            role="group"
            aria-label={t('comptabilite.flux.aria', { mois: f.moisLong(mois) })}
          >
            {dispo.traces.map((trace) => {
              const sur = actif === trace.cle;
              return (
                <g
                  key={trace.cle}
                  className={[styles.flux, sur ? styles.fluxActif : ''].join(' ')}
                  style={styleRang(trace.rang)}
                  role="button"
                  tabIndex={0}
                  aria-pressed={epingle === trace.cle}
                  aria-label={phrase(trace.lien)}
                  onMouseEnter={() => setSurvol(trace.cle)}
                  onMouseLeave={() => setSurvol(null)}
                  onFocus={() => setSurvol(trace.cle)}
                  onBlur={() => setSurvol(null)}
                  onClick={() =>
                    setEpingle((courant) => (courant === trace.cle ? null : trace.cle))
                  }
                  onKeyDown={(evenement) => surClavier(evenement, trace.cle)}
                >
                  <path
                    className={styles.fluxTrace}
                    d={trace.d}
                    strokeWidth={trace.epaisseur.toFixed(1)}
                  />
                  <path
                    className={styles.fluxPrise}
                    d={trace.d}
                    strokeWidth={Math.max(trace.epaisseur, 14).toFixed(1)}
                  />
                </g>
              );
            })}
            {dispo.gauche.map((noeud, i) => (
              <g key={noeud.id}>
                <rect
                  className={styles.noeud}
                  x={dispo.xG}
                  y={noeud.y.toFixed(1)}
                  width={dispo.nw}
                  height={noeud.h.toFixed(1)}
                  rx="2"
                />
                <text
                  className={styles.etiquette}
                  x={dispo.xG - 8}
                  y={((dispo.reperesG[i] ?? 0) - 2).toFixed(1)}
                  textAnchor="end"
                >
                  {nomService(noeud.id, true)}
                </text>
                <text
                  className={styles.etiquetteMontant}
                  x={dispo.xG - 8}
                  y={((dispo.reperesG[i] ?? 0) + 11).toFixed(1)}
                  textAnchor="end"
                >
                  {f.euros(noeud.valeur)}
                </text>
              </g>
            ))}
            {dispo.droite.map((noeud, i) => {
              const partie = parties.get(noeud.id)!;
              return (
                <g key={noeud.id} style={styleRang(partie.rang)}>
                  <rect
                    className={styles.noeudPartie}
                    x={dispo.xD}
                    y={noeud.y.toFixed(1)}
                    width={dispo.nw}
                    height={noeud.h.toFixed(1)}
                    rx="2"
                  />
                  <text
                    className={styles.etiquette}
                    x={dispo.xD + dispo.nw + 8}
                    y={((dispo.reperesD[i] ?? 0) - 2).toFixed(1)}
                  >
                    {tronquer(nomPartie(partie), dispo.etroit ? 14 : 26)}
                  </text>
                  <text
                    className={styles.etiquetteMontant}
                    x={dispo.xD + dispo.nw + 8}
                    y={((dispo.reperesD[i] ?? 0) + 11).toFixed(1)}
                  >
                    {f.euros(noeud.valeur)}
                  </text>
                </g>
              );
            })}
          </svg>
        )}
      </div>
      {dispo === null ? null : (
        <>
          <p className={styles.lecture} aria-live="polite">
            {lienActif ? phrase(lienActif) : t('comptabilite.flux.invite')}
          </p>
          <details className={styles.secours}>
            <summary>{t('comptabilite.flux.tableau')}</summary>
            <div className={commun.tableauConteneur}>
              <table className={commun.table}>
                <caption className={commun.srOnly}>{t('comptabilite.flux.tableauLegende')}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('comptabilite.flux.colService')}</th>
                    <th scope="col" className={commun.num}>
                      {t('comptabilite.flux.colHt')}
                    </th>
                    {schema.parties.map((partie) => (
                      <th key={partie.id} scope="col" className={commun.num}>
                        {nomPartie(partie)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {schema.services.map((service) => (
                    <tr key={service.service}>
                      <th scope="row">{nomService(service.service, false)}</th>
                      <td className={commun.num}>{f.euros(service.htCentimes)}</td>
                      {schema.parties.map((partie) => {
                        const lien = schema.liens.find(
                          (l) => l.service === service.service && l.partie === partie.id,
                        );
                        return (
                          <td key={partie.id} className={commun.num}>
                            {lien ? f.euros(lien.montantCentimes) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">{t('comptabilite.flux.total')}</th>
                    <td className={commun.num}>{f.euros(schema.totalCentimes)}</td>
                    {schema.parties.map((partie) => (
                      <td key={partie.id} className={commun.num}>
                        {f.euros(partie.montantCentimes)}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </details>
        </>
      )}
    </Section>
  );
}
